import type { Review, ReviewMedia } from './types';

export function formatPrice(kr: number): string {
  return `${kr.toLocaleString('sv-SE')} kr`;
}

export function formatRating(n: number): string {
  return n.toFixed(1).replace('.', ',');
}

export function reviewCountLabel(n: number): string {
  return `${n} ${n === 1 ? 'recension' : 'recensioner'}`;
}

export function clampRating(n: number): number {
  return Math.min(5, Math.max(1, Math.round(n)));
}

export function sortNewestFirst(reviews: Review[]): Review[] {
  return [...reviews].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

export function averageRating(reviews: Review[]): number {
  if (reviews.length === 0) return 0;
  return reviews.reduce((sum, r) => sum + clampRating(r.rating), 0) / reviews.length;
}

export function ratingDistribution(reviews: Review[]) {
  const total = reviews.length;
  return [5, 4, 3, 2, 1].map((stars) => {
    const count = reviews.filter((r) => clampRating(r.rating) === stars).length;
    return { stars, count, share: total ? count / total : 0 };
  });
}

/** Citatet under priset: den utvalda recensionen, annars den nyaste med 5 stjärnor. */
export function featuredReview(reviews: Review[]): Review | null {
  const withText = reviews.filter((r) => r.text && r.text.trim().length > 0);
  return (
    withText.find((r) => r.featured) ??
    sortNewestFirst(withText).find((r) => clampRating(r.rating) === 5) ??
    null
  );
}

/** Upp till `max` ansiktsbilder, bara från recensioner som har en. Samma bild visas aldrig två gånger. */
export function faceAvatars(reviews: Review[], max = 4) {
  const seen = new Set<string>();
  const out: { id: string; url: string }[] = [];
  for (const r of sortNewestFirst(reviews)) {
    const url = r.avatarUrl?.trim();
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push({ id: r.id, url });
    if (out.length >= max) break;
  }
  return out;
}

/**
 * Bildtext utan emoji först och sist, t.ex. "📸 Amanda M, 19" → "Amanda M, 19".
 * Texterna skrivs i admin; en färgad emoji passar inte sidans stil.
 */
export function captionText(text: string | null | undefined): string {
  const emoji = '(?:\\p{Extended_Pictographic}|\\p{Regional_Indicator}|[\\u{1F3FB}-\\u{1F3FF}\\u{FE0F}\\u{200D}\\u{20E3}])';
  return (text ?? '')
    .replace(new RegExp(`^(?:${emoji}|\\s)+`, 'u'), '')
    .replace(new RegExp(`(?:${emoji}|\\s)+$`, 'u'), '')
    .trim();
}

export interface MediaItem {
  key: string;
  name: string;
  media: ReviewMedia;
}

/** En media per recension (den första), och samma fil visas aldrig två gånger. */
export function mediaPerReview(reviews: Review[]): MediaItem[] {
  const seenUrls = new Set<string>();
  const out: MediaItem[] = [];
  for (const r of sortNewestFirst(reviews)) {
    const m = (r.media ?? []).find((x) => x.url && !seenUrls.has(x.url.trim()));
    if (!m) continue;
    seenUrls.add(m.url.trim());
    out.push({ key: `${r.id}:${m.url}`, name: r.name, media: m });
  }
  return out;
}
